import React, { useEffect, useState, useCallback, useRef } from 'react';
import WidgetHeader from './WidgetHeader';
import WidgetSidebar from './WidgetSidebar';
import ChatArea from './ChatArea';
import { WidgetWrapper, MainContent } from './styles';
import { useChatSession } from './hooks/useChatSession';
import { useVoiceCall } from '../../contexts/VoiceCallContext';

const AUTOPLAY_KEY = 'chat.voiceAutoplay';
const WELCOME_AUTOPLAYED_KEY = 'chat.welcomeAutoplayed';

interface WidgetContainerProps {
  isExpanded: boolean;
  isSidebarOpen: boolean;
  isMaximized: boolean;
  isDarkMode: boolean;
  onToggleSidebar: () => void;
  onToggleMaximize: () => void;
  onToggleDarkMode: () => void;
  onClose: () => void;
  onMessageReceived?: () => void;
}

const WidgetContainer: React.FC<WidgetContainerProps> = ({
  isExpanded,
  isSidebarOpen,
  isMaximized,
  isDarkMode,
  onToggleSidebar,
  onToggleMaximize,
  onToggleDarkMode,
  onClose,
  onMessageReceived,
}) => {
  const { session, isLoading, sendMessage, clearSession, initializeSession, audioCacheRef } =
    useChatSession();
  const { status: callStatus } = useVoiceCall();

  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(
    () => localStorage.getItem(AUTOPLAY_KEY) === 'true'
  );
  const [autoPlayTargetId, setAutoPlayTargetId] = useState<string | null>(null);
  const prevMessageCountRef = useRef(0);

  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlayEnabled(prev => {
      const next = !prev;
      localStorage.setItem(AUTOPLAY_KEY, String(next));
      return next;
    });
  }, []);

  const handleAutoPlayDone = useCallback(() => {
    setAutoPlayTargetId(null);
  }, []);

  // Initialize session when component mounts
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Pause auto-play when a voice call is active
  useEffect(() => {
    if (callStatus === 'connecting' || callStatus === 'connected') {
      setAutoPlayTargetId(null);
    }
  }, [callStatus]);

  // Auto-play new assistant messages
  useEffect(() => {
    if (!isAutoPlayEnabled) return;
    if (session.messages.length <= prevMessageCountRef.current) return;
    prevMessageCountRef.current = session.messages.length;
    const last = session.messages[session.messages.length - 1];
    if (last.role !== 'assistant' || last.isError) return;
    if (last.isWelcome) {
      if (!sessionStorage.getItem(WELCOME_AUTOPLAYED_KEY)) {
        sessionStorage.setItem(WELCOME_AUTOPLAYED_KEY, 'true');
        setAutoPlayTargetId(last.id);
      }
    } else {
      setAutoPlayTargetId(last.id);
    }
  }, [session.messages, isAutoPlayEnabled]);

  const onMessageReceivedRef = useRef(onMessageReceived);
  onMessageReceivedRef.current = onMessageReceived;

  // Notify parent when new assistant message is received and widget is closed
  useEffect(() => {
    if (session.messages.length > 0 && !isExpanded) {
      const lastMessage = session.messages[session.messages.length - 1];
      if (lastMessage.role === 'assistant' && !lastMessage.isWelcome) {
        onMessageReceivedRef.current?.();
      }
    }
  }, [session.messages, isExpanded]);

  const handleSendMessage = async (message: string) => {
    await sendMessage(message);
  };

  const handleQuestionClick = (question: string) => {
    handleSendMessage(question);
  };

  const handleNewChat = () => {
    prevMessageCountRef.current = 0;
    setAutoPlayTargetId(null);
    clearSession();
  };

  return (
    <WidgetWrapper isExpanded={isExpanded} isMaximized={isMaximized} isDarkMode={isDarkMode}>
      <WidgetHeader
        isDarkMode={isDarkMode}
        isSidebarOpen={isSidebarOpen}
        isAutoPlayEnabled={isAutoPlayEnabled}
        onToggleSidebar={onToggleSidebar}
        onToggleMaximize={onToggleMaximize}
        isMaximized={isMaximized}
        onToggleDarkMode={onToggleDarkMode}
        onToggleAutoPlay={toggleAutoPlay}
        onNewChat={handleNewChat}
        onClose={onClose}
      />
      <MainContent>
        <WidgetSidebar
          isOpen={isSidebarOpen}
          isDarkMode={isDarkMode}
          onQuestionClick={handleQuestionClick}
        />
        <ChatArea
          messages={session.messages}
          isLoading={isLoading}
          isDarkMode={isDarkMode}
          onSendMessage={handleSendMessage}
          audioCacheRef={audioCacheRef}
          autoPlayTargetId={autoPlayTargetId}
          onAutoPlayDone={handleAutoPlayDone}
        />
      </MainContent>
    </WidgetWrapper>
  );
};

export default WidgetContainer;
